import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NbCard, NbButton, Chip } from "@/components/nb";
import { AlertTriangle, ThumbsUp, MessageCircle, Sparkles, Plus, X, HelpCircle, Search } from "lucide-react";
import { toast } from "sonner";

function ProblemForm({ districtId, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (title.trim().length < 3) return toast.error("Give the problem a short title");
    if (body.trim().length < 10) return toast.error("Describe the problem in more detail");
    setBusy(true);
    try {
      const r = await api.post("/meghalaya/problems", { district_id: districtId, title, body });
      setTitle(""); setBody("");
      toast.success("Problem posted — the community can now add solutions.");
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };
  return (
    <NbCard color="tertiary" className="!p-4" data-testid="problem-form">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-5 h-5" />
        <div className="font-heading font-black text-lg tracking-tight">Spotted a problem? Post it.</div>
      </div>
      <input
        data-testid="problem-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="One-line title — e.g. Coal-tainted water in Lukha river"
        className="w-full px-3 py-2 nb-border rounded-lg bg-white mb-2"
      />
      <textarea
        data-testid="problem-body"
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Describe what you saw, where, who is affected, and how you know..."
        className="w-full px-3 py-2 nb-border rounded-lg bg-white"
      />
      <div className="mt-2 flex items-center gap-2">
        <NbButton onClick={submit} disabled={busy} color="black" data-testid="problem-submit">
          {busy ? "Posting…" : <><Plus className="w-4 h-4 inline mr-1" /> Post problem</>}
        </NbButton>
        <span className="font-mono-sl text-[10px] uppercase text-neutral-500">{body.length}/1500</span>
      </div>
    </NbCard>
  );
}

function SolutionForm({ problemId, onAdded }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (body.trim().length < 10) return toast.error("Write out your solution");
    setBusy(true);
    try {
      const r = await api.post(`/meghalaya/problems/${problemId}/solutions`, { body });
      setBody("");
      onAdded?.(r.data);
      toast.success("Solution added!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3" data-testid={`solution-form-${problemId}`}>
      <textarea
        rows={2}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add your solution — be specific, doable, and cite who does what..."
        className="w-full px-3 py-2 nb-border rounded-lg bg-white text-sm"
        data-testid={`solution-body-${problemId}`}
      />
      <div className="mt-1 flex items-center gap-2">
        <NbButton onClick={submit} disabled={busy} data-testid={`solution-submit-${problemId}`}>
          {busy ? "Adding…" : "Add solution"}
        </NbButton>
        <span className="font-mono-sl text-[10px] uppercase text-neutral-500">AI will suggest a follow-up question</span>
      </div>
    </div>
  );
}

function ProblemCard({ problem, onVote }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [solutions, setSolutions] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/meghalaya/problems/${problem.id}`);
      setSolutions(r.data.solutions || []);
    } catch { setSolutions([]); }
  };
  useEffect(() => { if (open && solutions === null) load(); }, [open]); // eslint-disable-line

  const addSolution = (s) => setSolutions((cur) => [s, ...(cur || [])]);
  const voteSolution = async (sid) => {
    if (!user) return toast.error("Sign in to vote");
    try {
      const r = await api.post(`/meghalaya/solutions/${sid}/vote`);
      setSolutions((cur) => (cur || []).map((s) => (s.id === sid ? { ...s, votes: r.data.votes, voters: r.data.voters } : s)));
    } catch { /* noop */ }
  };

  return (
    <NbCard className="!p-4" data-testid={`problem-${problem.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-full bg-sl-primary nb-border grid place-items-center">{problem.avatar_emoji || "🚀"}</div>
        <div className="text-sm font-semibold">{problem.author}</div>
        <button
          onClick={() => onVote?.(problem.id)}
          data-testid={`problem-vote-${problem.id}`}
          className="ml-auto flex items-center gap-1 text-sm nb-border px-2 py-1 rounded-full bg-white nb-hover">
          <ThumbsUp className="w-3 h-3" /> {problem.votes}
        </button>
      </div>
      <h4 className="font-heading font-black text-lg tracking-tight">{problem.title}</h4>
      <p className="mt-1 text-sm text-neutral-800">{problem.body}</p>

      {problem.sub_questions?.length > 0 && (
        <div className="mt-3 rounded-lg bg-sl-tertiary nb-border p-3" data-testid={`subqs-${problem.id}`}>
          <div className="flex items-center gap-1 font-mono-sl text-[10px] uppercase mb-1">
            <Sparkles className="w-3 h-3" /> AI wants you to think about
          </div>
          <ul className="space-y-1">
            {problem.sub_questions.map((q, i) => (
              <li key={i} className="flex gap-2 text-sm"><HelpCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{q}</span></li>
            ))}
          </ul>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`solutions-toggle-${problem.id}`}
        className="mt-3 flex items-center gap-1 font-mono-sl text-[10px] uppercase text-neutral-700 underline">
        <MessageCircle className="w-3 h-3" /> {problem.solutions_count || 0} solution{problem.solutions_count === 1 ? "" : "s"} {open ? "· hide" : "· show / add"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
            <SolutionForm problemId={problem.id} onAdded={(s) => { addSolution(s); }} />
            <div className="mt-3 space-y-2">
              {(solutions || []).map((s) => (
                <div key={s.id} className="rounded-lg nb-border bg-white p-3" data-testid={`solution-${s.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-sl-secondary nb-border grid place-items-center text-sm">{s.avatar_emoji || "💡"}</div>
                    <div className="text-sm font-semibold">{s.author}</div>
                    <button
                      onClick={() => voteSolution(s.id)}
                      data-testid={`solution-vote-${s.id}`}
                      className="ml-auto flex items-center gap-1 text-xs nb-border px-2 py-0.5 rounded-full bg-white nb-hover">
                      <ThumbsUp className="w-3 h-3" /> {s.votes}
                    </button>
                  </div>
                  <p className="text-sm">{s.body}</p>
                  {s.ai_critique && (
                    <div className="mt-2 flex gap-2 rounded-md bg-sl-accent nb-border p-2 text-xs" data-testid={`critique-${s.id}`}>
                      <Sparkles className="w-3 h-3 shrink-0 mt-0.5" />
                      <div><span className="font-mono-sl text-[10px] uppercase">AI mentor: </span>{s.ai_critique}</div>
                    </div>
                  )}
                </div>
              ))}
              {solutions !== null && solutions.length === 0 && (
                <div className="text-sm text-neutral-600 italic">No solutions yet — be the first!</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </NbCard>
  );
}

export default function ProblemBoard({ districtId, districtName }) {
  const { user } = useAuth();
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!districtId) return;
    setLoading(true);
    api.get(`/meghalaya/problems?district_id=${districtId}`)
      .then((r) => setProblems(r.data))
      .catch(() => setProblems([]))
      .finally(() => setLoading(false));
  }, [districtId]);

  const voteProblem = async (pid) => {
    if (!user) return toast.error("Sign in to vote");
    try {
      const r = await api.post(`/meghalaya/problems/${pid}/vote`);
      setProblems((cur) => cur.map((p) => (p.id === pid ? { ...p, votes: r.data.votes, voters: r.data.voters } : p)));
    } catch {}
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return problems;
    return problems.filter((p) => (p.title + " " + p.body).toLowerCase().includes(s));
  }, [q, problems]);

  return (
    <div className="mt-8" data-testid="community-board">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <AlertTriangle className="w-5 h-5" />
        <h3 className="font-heading font-black text-2xl tracking-tight">Community board · {districtName}</h3>
        <Chip color="secondary">NEW</Chip>
        <a
          href="/community"
          data-testid="community-global-link"
          className="ml-auto font-mono-sl text-[10px] uppercase underline"
        >Search all districts →</a>
      </div>
      <div className="text-sm text-neutral-700 mb-3">Post a real problem you see in {districtName}. Others will suggest solutions. AI adds smart follow-up questions to push everyone deeper.</div>

      {/* Local search */}
      <div className="mb-3 flex items-center gap-2 bg-white nb-border nb-shadow rounded-xl px-3 py-2 max-w-md">
        <Search className="w-4 h-4 shrink-0" />
        <input
          data-testid="board-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find a similar problem before posting..."
          className="flex-1 bg-transparent outline-none text-sm py-0.5"
        />
        {q && <button onClick={() => setQ("")} className="p-1 nb-hover" data-testid="board-clear"><X className="w-3 h-3" /></button>}
      </div>

      {user ? (
        <ProblemForm districtId={districtId} onCreated={(p) => setProblems((cur) => [p, ...cur])} />
      ) : (
        <NbCard className="!p-4"><div className="text-sm">Sign in to post a problem or add solutions.</div></NbCard>
      )}

      <div className="mt-4 space-y-3" data-testid="problem-list">
        {loading && <div className="font-mono-sl text-[10px] uppercase text-neutral-500">Loading problems…</div>}
        {!loading && filtered.length === 0 && problems.length > 0 && (
          <NbCard className="!p-4"><div className="text-sm">No matches in {districtName}. Try different keywords or <a href="/community" className="underline font-bold">search across all districts</a>.</div></NbCard>
        )}
        {!loading && problems.length === 0 && (
          <NbCard className="!p-4"><div className="text-sm">No problems posted yet — start the conversation!</div></NbCard>
        )}
        {filtered.map((p) => <ProblemCard key={p.id} problem={p} onVote={voteProblem} />)}
      </div>
    </div>
  );
}
